/* eslint-disable no-restricted-syntax */

import { RouterState } from '@estee/elc-router';
import { query, serviceQueries } from '@estee/elc-service-bus';

export const FORGOT_PASSWORD_ROUTE = 'forgotPassword';
export const RESET_PASSWORD_ROUTE = 'resetPassword';
export const CART_ROUTE = 'cart';
export const SHIPPING_ROUTE = 'shipping';
export const PAYMENT_ROUTE = 'payment';
export const ORDER_ROUTE = 'order';
export const CHECKOUT_NOT_ALLOWED_ROUTE = 'checkoutNotAllowed';
export const EXTERNAL_PAYMENT_ERROR_ROUTE = 'externalPaymentError';
export const EXTERNAL_PAYMENT_ERROR_GUEST_ROUTE = 'externalPaymentErrorGuest';
export const GUEST_ORDER_ROUTE = 'guestOrder';
export const ORDER_ASSOCIATE_ROUTE = 'associateOrder';
export const DEFAULT_ROUTE = 'default';
export const PAYMENT_ROUTE_PARAM = 'payment/:response';
export const NOT_FOUND_ROUTE = 'notFound';
export const CONFIRM_REGISTRATION_ROUTE = 'confirmRegistration';

const FORGOT_PASSWORD_ROUTE_PATTERN = '/forgot-password';
export const RESET_PASSWORD_PATH = '/reset-password';
const RESET_PASSWORD_ROUTE_PATTERN = `${RESET_PASSWORD_PATH}/:resetPasswordToken`;
const SHIPPING_ROUTE_PATTERN = '/shipping';
const PAYMENT_ROUTE_PATTERN = '/payment';
const PAYMENT_ROUTE_PARAM_PATTERN = '/payment/:response';
const ORDER_ROUTE_PATTERN = '/order/:orderId';
const GUEST_ORDER_ROUTE_PATTERN = '/guest-order/:orderId/:guestToken';
const ORDER_ASSOCIATE_ROUTE_PATTERN = '/associate-order/:orderId/:guestToken';
const CHECKOUT_NOT_ALLOWED_ROUTE_PATTERN = '/na';
const EXTERNAL_PAYMENT_ERROR_PATTERN = '/external-payment-error/:transactionId';
const EXTERNAL_PAYMENT_ERROR_GUEST_PATTERN =
    '/external-payment-error-guest/:transactionId/:guestToken';
const DEFAULT_ROUTE_PATTERN = '/';
export const CONFIRM_REGISTRATION_PATH = '/confirm-registration';
const CONFIRM_REGISTRATION_ROUTE_PATTERN = `${CONFIRM_REGISTRATION_PATH}/:confirmRegistrationToken`;
export const CART_ROUTE_PATTERN = '/cart';
export const ACCOUNT_ROUTE = 'account';
export const ACCOUNT_LANDING_ROUTE = 'accountLanding';
export const LOGIN_ROUTE = 'login';
export const SIGNUP_ROUTE = 'signUp';
export const ORDER_HISTORY_ROUTE = 'orderHistory';
export const ORDER_DETAILS_ROUTE = 'orderDetails';
export const CHECKOUT_ROUTE = 'checkout';

const ACCOUNT_ROUTE_PATTERN = '/account';
const ACCOUNT_LANDING_ROUTE_PATTERN = '/account-landing';
const LOGIN_ROUTE_PATTERN = '/login';
const SIGNUP_ROUTE_PATTERN = '/sign-up';
const ORDER_HISTORY_ROUTE_PATTERN = '/order-history';
const ORDER_DETAILS_ROUTE_PATTERN = '/order-details/:id';
const NOT_FOUND_ROUTE_PATTERN = '/not-found';
export const CHECKOUT_ROUTE_PATTERN = '/checkout';
export const DRUPAL_ACCOUNT_ROUTE = '/account#';

export const CARE_LANDING_ROUTE = 'care-landing';
export const CARE_LANDING_ROUTE_PATTERN = '/';
export const DRUPAL_CARE_ROUTE = '/care-landing';

export const CART_CONTEXT = 'cart';
export const CHECKOUT_CONTEXT = 'checkout';
export const ACCOUNT_CONTEXT = 'account';
export const CARE_LANDING_CONTEXT = 'care-landing';
export const MPP_ROUTE_CONTEXT = 'mpp';

export const STORES_ROUTE = 'stores';
export const STORES_ROUTE_PATTERN = '/stores';
export const STORES_CONTEXT = 'stores';

export const APPT_BOOKING_ROUTE = 'book-appointment';
export const APPT_BOOKING_ROUTE_PATTERN = '/book-appointment';
export const APPT_BOOKING_CONTEXT = 'appt-booking';

export const redirectToLoginIfNotAuthenticated = async (
    fromState: RouterState,
    toState: RouterState
) => {
    const isAuthenticated = await query(serviceQueries.IS_AUTHENTICATED);
    if (isAuthenticated) {
        return Promise.resolve();
    } else {
        return Promise.reject(new RouterState(LOGIN_ROUTE));
    }
};

export const redirectToAccountIfAuthenticated = async () => {
    const isAuthenticated = await query(serviceQueries.IS_AUTHENTICATED);
    if (!isAuthenticated) {
        return Promise.resolve();
    } else {
        return Promise.reject(new RouterState(ACCOUNT_LANDING_ROUTE));
    }
};

export const checkUserAuthenticatedOrGuest = async () => {
    const isAuthenticated = await query(serviceQueries.IS_AUTHENTICATED);
    const guestEmail = await query(serviceQueries.GUEST_EMAIL);
    const { checkout_allow_guest_user: isGuestAllowed } = await query(serviceQueries.GET_CONFIG, [
        'checkout_allow_guest_user'
    ]);

    const canProceed = isAuthenticated || (guestEmail && isGuestAllowed);

    if (canProceed) {
        return Promise.resolve();
    } else {
        return Promise.reject(new RouterState(CHECKOUT_NOT_ALLOWED_ROUTE));
    }
};

export interface IRoutes {
    cart: string;
    shipping: string;
    checkout: string;
    login: string;
    profile: string;
    settings: string;
    orderHistory: string;
    orderDetails: string;
    mpp: string;
    spp: string;
    apptBooking: string;
    stores: string;
    careLanding: string;
}

export const defaultAppRoutes = {
    cart: '/cart',
    shipping: '/checkout#/shipping',
    checkout: '/checkout',
    login: '/account#/login',
    profile: '/account#/account-landing',
    settings: '/account#/account',
    orderHistory: '/account#/order-history',
    orderDetails: '/account#/order-details',
    mpp: '',
    spp: '',
    apptBooking: '/book-appointment',
    stores: '/stores',
    careLanding: '/care-landing'
};
export const getAppRoutes = (routes: IRoutes = defaultAppRoutes) => {
    return [
        {
            name: DEFAULT_ROUTE,
            pattern: DEFAULT_ROUTE_PATTERN
        },
        {
            name: CART_ROUTE,
            pattern: CART_ROUTE_PATTERN,
            location: routes.cart,
            context: CART_CONTEXT
        },
        {
            name: SHIPPING_ROUTE,
            pattern: SHIPPING_ROUTE_PATTERN,
            beforeEnter: checkUserAuthenticatedOrGuest,
            location: routes.shipping,
            context: CHECKOUT_CONTEXT
        },
        {
            name: PAYMENT_ROUTE,
            pattern: PAYMENT_ROUTE_PATTERN,
            beforeEnter: checkUserAuthenticatedOrGuest,
            context: CHECKOUT_CONTEXT
        },
        {
            name: PAYMENT_ROUTE_PARAM,
            pattern: PAYMENT_ROUTE_PARAM_PATTERN,
            context: CHECKOUT_CONTEXT
        },
        {
            name: ORDER_ROUTE,
            pattern: ORDER_ROUTE_PATTERN,
            context: CHECKOUT_CONTEXT
        },
        {
            name: GUEST_ORDER_ROUTE,
            pattern: GUEST_ORDER_ROUTE_PATTERN,
            context: CHECKOUT_CONTEXT
        },
        {
            name: ORDER_ASSOCIATE_ROUTE,
            pattern: ORDER_ASSOCIATE_ROUTE_PATTERN,
            context: CHECKOUT_CONTEXT,
            location: routes.checkout
        },
        {
            name: CONFIRM_REGISTRATION_ROUTE,
            pattern: CONFIRM_REGISTRATION_ROUTE_PATTERN,
            context: ACCOUNT_CONTEXT
        },
        {
            name: LOGIN_ROUTE,
            pattern: LOGIN_ROUTE_PATTERN,
            beforeEnter: redirectToAccountIfAuthenticated,
            location: routes.login,
            context: ACCOUNT_CONTEXT
        },
        {
            name: ACCOUNT_LANDING_ROUTE,
            pattern: ACCOUNT_LANDING_ROUTE_PATTERN,
            beforeEnter: redirectToLoginIfNotAuthenticated,
            location: routes.profile,
            context: ACCOUNT_CONTEXT
        },
        {
            name: ACCOUNT_ROUTE,
            pattern: ACCOUNT_ROUTE_PATTERN,
            beforeEnter: redirectToLoginIfNotAuthenticated,
            location: routes.settings,
            context: ACCOUNT_CONTEXT
        },
        {
            name: SIGNUP_ROUTE,
            pattern: SIGNUP_ROUTE_PATTERN,
            beforeEnter: redirectToAccountIfAuthenticated,
            context: ACCOUNT_CONTEXT
        },
        {
            name: FORGOT_PASSWORD_ROUTE,
            pattern: FORGOT_PASSWORD_ROUTE_PATTERN,
            beforeEnter: redirectToAccountIfAuthenticated,
            context: ACCOUNT_CONTEXT
        },
        {
            name: RESET_PASSWORD_ROUTE,
            pattern: RESET_PASSWORD_ROUTE_PATTERN,
            beforeEnter: redirectToAccountIfAuthenticated,
            context: ACCOUNT_CONTEXT
        },
        {
            name: ORDER_HISTORY_ROUTE,
            pattern: ORDER_HISTORY_ROUTE_PATTERN,
            beforeEnter: redirectToLoginIfNotAuthenticated,
            location: routes.orderHistory,
            context: ACCOUNT_CONTEXT
        },
        {
            name: ORDER_DETAILS_ROUTE,
            pattern: ORDER_DETAILS_ROUTE_PATTERN,
            beforeEnter: redirectToLoginIfNotAuthenticated,
            context: ACCOUNT_CONTEXT,
            location: routes.orderDetails
        },
        {
            name: CHECKOUT_ROUTE,
            pattern: CHECKOUT_ROUTE_PATTERN,
            location: routes.checkout,
            context: CHECKOUT_CONTEXT
        },
        {
            name: EXTERNAL_PAYMENT_ERROR_ROUTE,
            pattern: EXTERNAL_PAYMENT_ERROR_PATTERN,
            context: CHECKOUT_CONTEXT,
            location: routes.checkout
        },
        {
            name: EXTERNAL_PAYMENT_ERROR_GUEST_ROUTE,
            pattern: EXTERNAL_PAYMENT_ERROR_GUEST_PATTERN,
            context: CHECKOUT_CONTEXT,
            location: routes.checkout
        },
        {
            name: CHECKOUT_NOT_ALLOWED_ROUTE,
            pattern: CHECKOUT_NOT_ALLOWED_ROUTE_PATTERN,
            context: CHECKOUT_CONTEXT,
            location: routes.checkout
        },
        {
            name: CARE_LANDING_ROUTE,
            pattern: CARE_LANDING_ROUTE_PATTERN,
            context: CARE_LANDING_CONTEXT,
            location: routes.careLanding
        },
        {
            name: APPT_BOOKING_ROUTE,
            pattern: APPT_BOOKING_ROUTE_PATTERN,
            context: APPT_BOOKING_CONTEXT,
            location: routes.apptBooking
        },
        {
            name: STORES_ROUTE,
            pattern: STORES_ROUTE_PATTERN,
            context: STORES_CONTEXT,
            location: routes.stores
        },
        {
            name: NOT_FOUND_ROUTE,
            pattern: NOT_FOUND_ROUTE_PATTERN
        }
    ];
};
